import { motion } from "framer-motion";
import { PhoneMissed, Users, Heart, Zap } from "lucide-react";

const POS_IMG =
  "https://images.pexels.com/photos/12935078/pexels-photo-12935078.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200";

const benefits = [
  {
    icon: PhoneMissed,
    title: "Never miss a call during the rush",
    body: "When the dinner rush hits and every line is ringing, the AI picks up instantly, so no order slips away.",
  },
  {
    icon: Users,
    title: "Give your staff a break from the phones",
    body: "Your team stays focused on the food and the guests in front of them, not the phone ringing all shift.",
  },
  {
    icon: Heart,
    title: "Make every caller feel looked after",
    body: "Every guest gets a warm greeting and a smooth, unhurried order, the kind of friendly service that turns first-time callers into regulars.",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const WhyItHelps = () => {
  return (
    <section className="relative py-24 md:py-32" data-testid="why-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-14 md:mb-20"
        >
          <span className="text-terra font-heading text-sm font-semibold tracking-widest uppercase">Why it helps</span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-cream">
            Built for the way your kitchen actually runs.
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {benefits.map((b, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="md:col-span-4 group border border-line bg-surface p-8 flex flex-col justify-between min-h-[280px] hover:border-terra/50 transition-colors duration-500"
              data-testid={`benefit-card-${i}`}
            >
              <b.icon className="w-8 h-8 text-terra" strokeWidth={1.5} />
              <div>
                <h3 className="font-heading text-xl sm:text-2xl font-medium text-cream mb-3">{b.title}</h3>
                <p className="text-sand leading-relaxed">{b.body}</p>
              </div>
            </motion.div>
          ))}

          <motion.div
            custom={3}
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="md:col-span-12 grid grid-cols-1 md:grid-cols-12 gap-6"
            data-testid="benefit-card-3"
          >
            <div className="md:col-span-7 border border-line bg-terra p-8 md:p-12 flex flex-col justify-center min-h-[240px]">
              <Zap className="w-8 h-8 text-ink mb-6" strokeWidth={1.5} />
              <h3 className="font-heading text-2xl sm:text-3xl md:text-4xl font-medium text-ink tracking-tight">
                Every order lands right in your POS. No extra work for your team.
              </h3>
            </div>
            <div className="md:col-span-5 border border-line overflow-hidden min-h-[240px] relative">
              <img
                src={POS_IMG}
                alt="Restaurant point-of-sale tablet on a counter"
                className="absolute inset-0 w-full h-full object-cover scale-105 hover:scale-100 transition-transform duration-[1200ms]"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyItHelps;
